import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import ModalContext from 'contexts/ModalContext'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import ChatPanel from 'tripPlanner/assistant/components/AssistantChatPanel'
import MessagesList from 'tripPlanner/components/Chat/MessagesList'
import ChatMessageInput from 'tripPlanner/components/Chat/ChatMessageInput'
import AddBookingModal from 'tripPlanner/components/Chat/AddBookingModal'
import ChatInputContextBar from '../components/ChatInputContextBar'
import AiContextTrace, { type TracePoint } from '../components/AiContextTrace'
import SalesAgentSummaryPanel from '../components/SalesAgentSummaryPanel'
import ChatFeedbackPopup from '../components/ChatFeedbackPopup'
import { ChatMessageActionsProvider, type OfferClickSearchParams } from '../contexts/ChatMessageActionsContext'
import type { LocationInfoData } from 'tripPlanner/components/Chat/Message'
import { useChatFiles } from '../contexts/ChatFilesContext'
import { InputWrapper } from '../styles/layoutStyles'
import type { HotelMapViewData, ExperienceMapViewData } from 'tripPlanner/api/types'
import { useClipboard } from '../hooks/useClipboard'
import { useFeedback } from '../hooks/useFeedback'
import { getChatErrorMessage, getChatErrorHeading } from '../utils/chatErrorMessages'
import { formatDayLabel, getLocalisedDayLabel } from 'tripPlanner/utils/chatDates'
import { useTripPlannerEventBus, useEventSubscription } from '../contexts/TripPlannerEventBus'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useChatStreamStatus } from '../contexts/ChatStreamStatusContext'
import { useChatScrollContext } from '../contexts/ChatScrollContext'
import { useChatStreamMessages } from '../contexts/ChatStreamMessagesContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { useMapInteraction } from '../contexts/MapInteractionContext'
import { useChatFeedbackPopup } from '../hooks/useChatFeedbackPopup'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import BodyText from 'LuxKit/Typography/BodyText'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { appendContextTag } from 'tripPlanner/utils/contextTag'
import type { MappedRequestAgentCallbackResult } from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import { formatCallbackTimeLabel } from 'supportAssistant/components/RequestAgentCallback/RequestAgentCallbackUtils'
import { shouldDefaultSalesSummaryCollapsed } from '../utils/salesAgentSummary'
import noop from 'lib/function/noop'

interface Props {
  chatId: number | undefined
  isLoggedIn: boolean
  isDevMode: boolean
  isSpoofed: boolean
  spoofingAgentEmail?: string
  navigateToNewChatRoute: () => void
  handleSubmit: (message: string) => Promise<void>
  handleMultiChoiceSelectionChange: (selection: Array<string>) => void
  activeMultiChoiceSelection: Array<string>
  prefilledSelectionTags?: Array<string>
  mobileSelectionText: string | undefined
  offerContextTag: { label: string; offerId: string } | undefined
  handleRemoveTag: (tag: string) => void
  onRemovePrefilledSelectionTag?: (tag: string) => void
  selectedBookingItemIds?: Array<string>
  onBookingItemsAdded?: (bookingItemIds: Array<string>) => void
  chatInputRef: React.RefObject<HTMLTextAreaElement | null>
  autoFocusChatInput?: boolean
  compactDisclaimer?: boolean
  enableMapActions?: boolean
  fitAvailableHeight?: boolean
}

const DEFAULT_PREFILLED_SELECTION_TAGS: Array<string> = []
const DEFAULT_SELECTED_BOOKING_ITEM_IDS: Array<string> = []

interface ChatInputContext {
  label: string
  message: string
  placeholder?: string
}

function ChatContainer({
  chatId,
  isLoggedIn,
  isDevMode,
  isSpoofed,
  spoofingAgentEmail,
  navigateToNewChatRoute,
  handleSubmit,
  handleMultiChoiceSelectionChange,
  activeMultiChoiceSelection,
  prefilledSelectionTags = DEFAULT_PREFILLED_SELECTION_TAGS,
  mobileSelectionText,
  offerContextTag,
  handleRemoveTag,
  onRemovePrefilledSelectionTag = noop,
  selectedBookingItemIds = DEFAULT_SELECTED_BOOKING_ITEM_IDS,
  onBookingItemsAdded,
  chatInputRef,
  autoFocusChatInput = true,
  compactDisclaimer = false,
  enableMapActions = true,
  fitAvailableHeight = false,
}: Props) {
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const eventBus = useTripPlannerEventBus()
  const showModal = useContext(ModalContext)
  const { onCardHover } = useMapInteraction()
  const { files, setFiles } = useChatFiles()

  const { stopStreaming, regenerateResponse: streamingRegenerate, sendPostAuthMessage, setFollowTail, submitMessage } = useChatActions()
  const { isStreaming, isStreamOpen, loadingState, isStalled, isOnline, reasoningSummaries, toolCallInfo } = useChatStreamStatus()
  const { handleScroll: handleMessagesScroll, isAtBottom, hasContentOutOfView, enableFollowAndScrollToBottom, dynamicSpacerHeight, messagesWrapperRef } = useChatScrollContext()

  const {
    messages,
    filteredMessages,
    isChatError,
    chatError,
    userFeedbackMap,
    setUserFeedbackMap,
  } = useChatStreamMessages()

  const { itineraryData, isFlightCartMutationPending } = useItineraryContext()

  // ── Local state ──

  const [messageInputValue, setMessageInputValue] = useState('')
  const [chatInputContext, setChatInputContext] = useState<ChatInputContext | undefined>(undefined)
  const [contextTrace, setContextTrace] = useState<{ origin: TracePoint; key: number } | undefined>(undefined)
  const contextBarRef = useRef<HTMLDivElement>(null)
  const traceKeyRef = useRef(0)
  const historyOverlayTouchStartYRef = useRef<number | undefined>(undefined)

  // ── Derived values ──

  const isLoading = loadingState.status === 'loadingHistory' || loadingState.status === 'preparingStream'
  const isListLoading = loadingState.isListLoading
  const isUpdatingItinerary = loadingState.isUpdatingItinerary
  const activeToolLoadingKey = loadingState.activeToolLoadingKey
  const shouldShowSalesSummary = isSpoofed && chatId !== undefined
  const shouldStartSalesSummaryCollapsed = shouldDefaultSalesSummaryCollapsed(spoofingAgentEmail)

  const messagesWithoutInlineMap = useMemo(() => {
    return filteredMessages.map(msg => ({
      ...msg,
      parts: msg.parts.filter(part => {
        if (part.type !== 'ui_component') return true
        const data = part.data
        if (data && typeof data === 'object' && 'type' in data) {
          return data.type !== 'location_entities'
        }
        return true
      }),
    }))
  }, [filteredMessages])

  const lastAssistantMessage = useMemo(() => {
    return filteredMessages.findLast(m => m.role === 'assistant')
  }, [filteredMessages])

  const hasUserMessage = useMemo(() => {
    return filteredMessages.some(m => m.role === 'user')
  }, [filteredMessages])

  const lastMessageIsAssistant = filteredMessages.length > 0 && filteredMessages[filteredMessages.length - 1].role === 'assistant'
  const shouldShowAssistantActions = !!lastAssistantMessage && lastMessageIsAssistant && !isLoading && !isStreaming

  const hasMultiChoiceSelection = activeMultiChoiceSelection.length > 0
  const hasPrefilledSelection = prefilledSelectionTags.length > 0
  const hasMobileSelection = mobileSelectionText !== undefined
  const hasFiles = files.length > 0
  const hasMessageContent = messageInputValue.trim() !== '' || hasMultiChoiceSelection || hasPrefilledSelection || hasMobileSelection || hasFiles
  const showStopButton = isStreaming || isStreamOpen
  const disableSubmit = isLoading || showStopButton || isFlightCartMutationPending || !hasMessageContent

  const allSelectedTags = useMemo(() => {
    const tags = [...prefilledSelectionTags, ...activeMultiChoiceSelection]
    if (mobileSelectionText) {
      tags.push(mobileSelectionText)
    }
    if (offerContextTag) {
      tags.push(offerContextTag.label)
    }
    return [...new Set(tags)]
  }, [activeMultiChoiceSelection, mobileSelectionText, offerContextTag, prefilledSelectionTags])

  const handleRemoveSelectedTag = useCallback((tag: string) => {
    if (prefilledSelectionTags.includes(tag)) {
      onRemovePrefilledSelectionTag(tag)
      if (activeMultiChoiceSelection.includes(tag)) {
        handleRemoveTag(tag)
      }
      return
    }

    handleRemoveTag(tag)
  }, [activeMultiChoiceSelection, handleRemoveTag, onRemovePrefilledSelectionTag, prefilledSelectionTags])

  // ── Chat error effect ──

  useEffect(() => {
    if (isChatError && chatId !== undefined) {
      const errorStatus = chatError && typeof chatError === 'object' && 'status' in chatError && typeof chatError.status === 'number'
        ? chatError.status
        : undefined
      showSnackbar(
        getChatErrorMessage(intl, errorStatus),
        'critical',
        { heading: getChatErrorHeading(intl) },
      )
      navigateToNewChatRoute()
    }
  }, [isChatError, chatId, chatError, navigateToNewChatRoute, intl])

  // ── Hooks ──

  const { copiedRecently, handleCopyResponse } = useClipboard({ lastAssistantMessage, isDevMode })

  const handleFeedbackSubmitted = useCallback((messageId: string, reaction: 'UP' | 'DOWN') => {
    setUserFeedbackMap(prev => ({
      ...prev,
      [messageId]: reaction,
    }))
  }, [setUserFeedbackMap])

  const { feedbackSubmitted, handleThumbsUp, handleThumbsDown } = useFeedback({
    chatId,
    lastAssistantMessageId: lastAssistantMessage?.id,
    existingFeedback: userFeedbackMap,
    onFeedbackSubmitted: handleFeedbackSubmitted,
  })
  const feedbackPopup = useChatFeedbackPopup({ chatId, messages })

  // ── Callbacks ──

  const focusChatInput = useCallback(() => {
    chatInputRef.current?.focus()
  }, [chatInputRef])

  // Landing point for the Ask AI context comet: the left edge of the context chip.
  const getContextTraceTarget = useCallback((): TracePoint | null => {
    const rect = contextBarRef.current?.getBoundingClientRect()
    if (!rect || rect.height === 0) return null
    return { x: rect.left + 32, y: rect.top + rect.height * 0.42 }
  }, [])

  const handleContextTraceDone = useCallback(() => {
    setContextTrace(undefined)
  }, [])

  const handleClearChatInputContext = useCallback(() => {
    setChatInputContext(undefined)
  }, [])

  const handleRegenerateResponse = useCallback(async() => {
    if (isLoading) return
    if (!lastAssistantMessage) return
    const lastUserMessage = messages.findLast((message) => message.role === 'user')
    if (!lastUserMessage) return
    setFollowTail(false)
    return streamingRegenerate()
  }, [isLoading, lastAssistantMessage, messages, streamingRegenerate, setFollowTail])

  const handleCallbackRequestSubmitted = useCallback(async(results: MappedRequestAgentCallbackResult) => {
    const { callbackDate, callbackTime } = results.customer
    const formattedTime = formatCallbackTimeLabel(callbackTime, intlDateFormatter)
    const formattedDate = callbackDate
      ? intlDateFormatter(callbackDate, INTL_DMY_CASUAL_FORMAT)
      : ''

    if (!formattedTime || !formattedDate) {
      return
    }

    await submitMessage(
      intl.formatMessage(
        {
          id: '1JIZSmwFqLHg37YN0HnK',
          defaultMessage: 'I scheduled a callback for {time} on {date}.',
          description: 'User chat message sent automatically after successfully scheduling a callback in trip planner chat',
        },
        {
          time: formattedTime,
          date: formattedDate,
        },
      ),
    )
  }, [intl, intlDateFormatter, submitMessage])

  const handleScheduleCallback = useCallback(() => {
    showModal(
      <RequestAgentCallbackModal
        type="SALES"
        onRequestSubmitted={handleCallbackRequestSubmitted}
        messagePlaceholder={intl.formatMessage({
          id: 'i-4TtHQw-VV2kmnT-QDB',
          defaultMessage: "e.g. I'd love help turning my itinerary into a real trip",
          description: 'Custom message placeholder shown in the request callback modal when opened from trip planner chat',
        })}
      />,
    )
  }, [handleCallbackRequestSubmitted, intl, showModal])

  const handleSubmitWithChatInputContext = useCallback((message: string) => {
    if (chatInputContext) {
      const messageWithContext = appendContextTag(
        message,
        chatInputContext.message,
        chatInputContext.label,
      )
      setChatInputContext(undefined)
      return handleSubmit(messageWithContext)
    }
    return handleSubmit(message)
  }, [chatInputContext, handleSubmit])

  const handleAddBookingClick = useCallback(() => {
    showModal(
      <AddBookingModal
        onSendMessage={handleSubmitWithChatInputContext}
        disabledBookingItemIds={selectedBookingItemIds}
        onBookingItemsAdded={onBookingItemsAdded}
        isSubmitLocked={isStreaming}
        chatId={chatId}
      />,
    )
  }, [chatId, handleSubmitWithChatInputContext, isStreaming, onBookingItemsAdded, selectedBookingItemIds, showModal])

  // ── History overlay scroll forwarding ──

  const handleHistoryOverlayWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    if (!messagesWrapperRef.current) {
      return
    }
    event.preventDefault()
    messagesWrapperRef.current.scrollTop += event.deltaY
  }, [messagesWrapperRef])

  const handleHistoryOverlayTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    const firstTouch = event.touches[0]
    if (!firstTouch) {
      return
    }
    historyOverlayTouchStartYRef.current = firstTouch.clientY
  }, [])

  const handleHistoryOverlayTouchMove = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    if (!messagesWrapperRef.current || historyOverlayTouchStartYRef.current === undefined) {
      return
    }

    const touch = event.touches[0]
    if (!touch) {
      return
    }

    const deltaY = historyOverlayTouchStartYRef.current - touch.clientY
    if (deltaY !== 0) {
      messagesWrapperRef.current.scrollTop += deltaY
      historyOverlayTouchStartYRef.current = touch.clientY
    }
    event.preventDefault()
  }, [messagesWrapperRef])

  const handleHistoryOverlayTouchEnd = useCallback(() => {
    historyOverlayTouchStartYRef.current = undefined
  }, [])

  // ── ChatMessageActionsProvider callbacks ──

  const handleHotelMapViewClick = useCallback((data: HotelMapViewData) => {
    eventBus.dispatch({ type: 'show_hotel_on_map', data })
  }, [eventBus])

  const handleExperienceMapViewClick = useCallback((data: ExperienceMapViewData) => {
    eventBus.dispatch({ type: 'show_experience_on_map', data })
  }, [eventBus])

  const handleOfferClick = useCallback((data: { offerId: string; searchParams?: OfferClickSearchParams; offerType?: string }) => {
    eventBus.dispatch({ type: 'open_offer_drawer', ...data })
  }, [eventBus])

  const handlePlaceClick = useCallback((data: { googlePlaceId: string; placeName: string }) => {
    eventBus.dispatch({ type: 'open_place_drawer', ...data })
  }, [eventBus])

  const handlePlaceSearchClick = useCallback((data: { searchQuery: string; placeName: string }) => {
    eventBus.dispatch({ type: 'open_place_search_drawer', ...data })
  }, [eventBus])

  const handleLocationInfoClick = useCallback((data: LocationInfoData) => {
    eventBus.dispatch({ type: 'open_location_drawer', data })
  }, [eventBus])

  // ── Event bus subscriptions ──

  useEventSubscription('ask_ai_for_day', (event) => {
    const dayLabel = event.date
      ? `${formatDayLabel(event.date)} (${getLocalisedDayLabel(intl, event.dayNumber)})`
      : getLocalisedDayLabel(intl, event.dayNumber)
    const label = intl.formatMessage(
      {
        id: 'WWQHyN9c4jvJQITwN7jQ',
        defaultMessage: '{dayLabel} - {title}',
        description: 'Day context bar label showing day label and title in trip planner chat',
      },
      { dayLabel, title: event.title },
    )
    const placeholder = intl.formatMessage(
      {
        id: '7eTuFAD860P0YRHQhFcJ',
        defaultMessage: 'Ask me anything about {dayLabel}',
        description: 'Chat input placeholder when user is asking about a specific day in the trip itinerary',
      },
      { dayLabel: getLocalisedDayLabel(intl, event.dayNumber, event.date) },
    )
    setChatInputContext({
      label,
      message: `The user is asking about ${dayLabel}: ${event.title}`,
      placeholder,
    })
    focusChatInput()
  })

  useEventSubscription('ask_ai_for_item', (event) => {
    const placeholder = intl.formatMessage(
      {
        id: 'HsaECJgtZ2iQBD6C8nTE',
        defaultMessage: 'Ask me anything about {name}',
        description: 'Chat input placeholder when the user is asking about a specific itinerary item in the trip planner',
      },
      { name: event.name },
    )
    setChatInputContext({
      label: event.name,
      message: event.category
        ? `The user is asking about ${event.name} (${event.category})`
        : `The user is asking about ${event.name}`,
      placeholder,
    })
    focusChatInput()
  })

  useEventSubscription('ask_ai_for_list_row', (event) => {
    const placeholder = intl.formatMessage(
      {
        id: 'FX7E5Rf2eGbjg4NB328V',
        defaultMessage: 'Ask me anything about {title}',
        description: 'Chat input placeholder when user is asking about a specific row in the trip planner list view',
      },
      { title: event.title },
    )
    setChatInputContext({
      label: event.title,
      message: event.context,
      placeholder: event.placeholder ?? placeholder,
    })
    focusChatInput()
    if (event.origin) {
      traceKeyRef.current += 1
      setContextTrace({ origin: event.origin, key: traceKeyRef.current })
    }
  })

  useEventSubscription('reset_chat_state', () => {
    setMessageInputValue('')
    setChatInputContext(undefined)
  })

  // ── i18n ──

  const chatInputPlaceholder = chatInputContext?.placeholder
  const messagesErrorText = intl.formatMessage({
    id: 'zNUAhjKCUvuU-8zm4DQm',
    defaultMessage: 'Something went wrong displaying messages.',
    description: 'Fallback error message shown if trip planner chat messages fail to render',
  })

  // ── Render ──

  const inputContent = (
    <InputWrapper>
      {feedbackPopup.isVisible && (
        <ChatFeedbackPopup
          onClose={feedbackPopup.dismiss}
          onSubmit={feedbackPopup.submit}
        />
      )}
      <div ref={contextBarRef}>
        {chatInputContext && (
          <ChatInputContextBar
            label={chatInputContext.label}
            onClose={handleClearChatInputContext}
          />
        )}
      </div>
      {contextTrace && (
        <AiContextTrace
          key={contextTrace.key}
          origin={contextTrace.origin}
          getTarget={getContextTraceTarget}
          onDone={handleContextTraceDone}
        />
      )}
      <ChatMessageInput
        onSubmit={handleSubmitWithChatInputContext}
        value={messageInputValue}
        onChange={setMessageInputValue}
        disableSubmit={disableSubmit}
        isStreaming={isStreaming}
        showStopButton={showStopButton}
        onStopStream={stopStreaming}
        files={files}
        onFilesChange={setFiles}
        selectedTags={allSelectedTags}
        onAddBookingClick={handleAddBookingClick}
        showAddBookingAction={isLoggedIn}
        onRemoveTag={handleRemoveSelectedTag}
        inputRef={chatInputRef}
        placeholder={chatInputPlaceholder}
        autoFocus={autoFocusChatInput}
        compactDisclaimer={compactDisclaimer}
        chatId={chatId}
      />
    </InputWrapper>
  )

  return (
    <ChatMessageActionsProvider
      onMultiChoiceSelectionChange={handleMultiChoiceSelectionChange}
      onOfferClick={handleOfferClick}
      onOfferHover={onCardHover}
      onHotelMapViewClick={enableMapActions ? handleHotelMapViewClick : noop}
      onExperienceMapViewClick={enableMapActions ? handleExperienceMapViewClick : noop}
      onPlaceClick={handlePlaceClick}
      onPlaceSearchClick={handlePlaceSearchClick}
      onLocationInfoClick={handleLocationInfoClick}
      onScheduleCallback={handleScheduleCallback}
      onSendMessage={handleSubmit}
      onAuthenticatedFollowUp={sendPostAuthMessage}
      itineraryData={itineraryData}
      isDevMode={isDevMode}
    >
      <ChatPanel
        messagesWrapperRef={messagesWrapperRef}
        onScroll={handleMessagesScroll}
        isOverlay={false}
        showHistoryOverlay={false}
        onHistoryWheel={handleHistoryOverlayWheel}
        onHistoryTouchStart={handleHistoryOverlayTouchStart}
        onHistoryTouchMove={handleHistoryOverlayTouchMove}
        onHistoryTouchEnd={handleHistoryOverlayTouchEnd}
        inputSlot={inputContent}
        isAtBottom={isAtBottom}
        hasContentOutOfView={hasContentOutOfView}
        onScrollToBottomButtonClick={enableFollowAndScrollToBottom}
        dynamicSpacerHeight={dynamicSpacerHeight}
        fitAvailableHeight={fitAvailableHeight}
        topSlot={
          <SalesAgentSummaryPanel
            chatId={chatId}
            enabled={shouldShowSalesSummary}
            initiallyCollapsed={shouldStartSalesSummaryCollapsed}
          />
        }
      >
        <ErrorBoundary fallback={() => <BodyText variant="medium" colour="critical">{messagesErrorText}</BodyText>}>
          <MessagesList
            messages={messagesWithoutInlineMap}
            isLoading={isListLoading}
            isUpdatingItinerary={isUpdatingItinerary}
            activeToolLoadingKey={activeToolLoadingKey}
            isStalled={isStalled}
            isOnline={isOnline}
            reasoningSummaries={reasoningSummaries}
            toolCallInfo={toolCallInfo}
            loadingStatus={loadingState.status}
            showAssistantActions={shouldShowAssistantActions}
            onRegenerate={handleRegenerateResponse}
            onCopy={handleCopyResponse}
            disableRegenerateButton={isLoading || !hasUserMessage}
            disableCopyButton={!lastAssistantMessage || isLoading}
            copiedRecently={copiedRecently}
            onThumbsUp={handleThumbsUp}
            onThumbsDown={handleThumbsDown}
            feedbackSubmitted={feedbackSubmitted}
            externalSelection={activeMultiChoiceSelection}
          />
        </ErrorBoundary>
      </ChatPanel>
    </ChatMessageActionsProvider>
  )
}

export default ChatContainer
